const values = [
  {
    icon: "ri-eye-line",
    title: "Full Transparency",
    description:
      "No hidden markups, no mystery suppliers. You see every quote, every supplier profile, and every step of the process before committing to anything.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Built for Speed",
    description:
      "We move as fast as we responsibly can. Quick supplier matching, prompt quote comparisons, and no unnecessary delays — we built our process around your timelines, not ours.",
  },
  {
    icon: "ri-shake-hands-line",
    title: "True Partnership",
    description:
      "We don't hand you a supplier list and disappear. Our team stays active in every deal — from first contact through final delivery and payment.",
  },
];

export default function AboutSection() {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Main Split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">

          {/* Image Side */}
          <div className="relative">
            <div className="relative w-full h-[480px] rounded-3xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/787168b8635ffd0e9660a0b771a2d610.jpg"
                alt="LTF Sourcing team"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#222831]/70 via-transparent to-transparent" />
            </div>

            {/* Floating stat card */}
            <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-[#222831]/90 backdrop-blur-sm p-5 flex items-center gap-5">
              <div className="flex-1 border-r border-white/10 pr-5">
                <p className="text-2xl font-extrabold text-[#C9A227]">75+</p>
                <p className="text-white/60 text-xs mt-0.5">Businesses Served</p>
              </div>
              <div className="flex-1 border-r border-white/10 pr-5">
                <p className="text-2xl font-extrabold text-[#C9A227]">8+</p>
                <p className="text-white/60 text-xs mt-0.5">Countries Active</p>
              </div>
              <div className="flex-1">
                <p className="text-2xl font-extrabold text-[#C9A227]">3</p>
                <p className="text-white/60 text-xs mt-0.5">Core Service Pillars</p>
              </div>
            </div>

            {/* Gold accent bar */}
            <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C9A227] ">
              <i className="ri-links-line text-white text-xs"></i>
              <span className="text-white text-xs font-bold uppercase tracking-widest">LTF Sourcing</span>
            </div>
          </div>

          {/* Text Side */}
          <div className="flex flex-col gap-6">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-[#C9A227] mb-3 block">
                About Us
              </span>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-[#222831] leading-tight">
                Built to Remove Every Barrier Between Your Business and the Right Supply
              </h2>
            </div>

            <p className="text-base text-[#3A506B] leading-relaxed">
              LTF Sourcing was founded on a single belief: that growing businesses
              shouldn&apos;t have to navigate the complexity of global supply alone.
              Whether you&apos;re tracking down a reliable manufacturer, securing
              wholesale pricing, or clearing excess inventory — we built the systems,
              the network, and the expertise to make it straightforward.
            </p>

            <p className="text-base text-[#3A506B] leading-relaxed">
              Since launching, LTF Sourcing has grown into a full-service procurement
              partner with an active presence across 8+ countries.
              We&apos;ve already worked with 75+ businesses — from early-stage startups to
              growing procurement teams — and we&apos;re expanding our network every month.
            </p>

            {/* New Company Trust Builder */}
            <div className="rounded-2xl bg-[#C9A227]/10 border border-[#C9A227]/30 p-6 flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C9A227] flex-shrink-0">
                <i className="ri-sparkling-line text-white text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-bold text-[#222831] mb-1">We&apos;re New — And That&apos;s Good for You</h4>
                <p className="text-sm text-[#3A506B] leading-relaxed">
                  We don&apos;t have layers of bureaucracy or outdated processes. Every client gets 
                  direct access to our founding team. We&apos;re hungry to prove ourselves, 
                  responsive to feedback, and building our reputation one successful deal at a time. 
                  When you work with us, you&apos;re not a number — you&apos;re a partner in our growth story.
                </p>
              </div>
            </div>

            {/* Pillars reference */}
            <div className="flex flex-col gap-3 pt-2">
              {[
                {
                  icon: "ri-search-eye-line",
                  pillar: "Product Sourcing",
                  detail: "A growing network of verified global suppliers, matched quickly.",
                },
                {
                  icon: "ri-store-3-line",
                  pillar: "Wholesale Supply",
                  detail: "Direct manufacturer access across 40+ product categories.",
                },
                {
                  icon: "ri-archive-drawer-line",
                  pillar: "Inventory Solutions",
                  detail: "Connecting surplus inventory with qualified bulk buyers.",
                },
              ].map((item) => (
                <div key={item.pillar} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A227]/10 flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-[#C9A227] text-sm`}></i>
                  </div>
                  <div>
                    <span className="text-sm font-bold text-[#222831]">{item.pillar}</span>
                    <span className="text-sm text-[#3A506B]"> — {item.detail}</span>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => {
                const el = document.querySelector("#services");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="self-start mt-2 px-7 py-3 rounded-full bg-[#222831] text-white text-sm font-bold hover:bg-[#3A506B] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              Explore Our Services
              <i className="ri-arrow-right-line text-base"></i>
            </button>
          </div>
        </div>

        {/* Values Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {values.map((v) => (
            <div
              key={v.title}
              className="rounded-2xl bg-[#D9E2EC] border border-[#c4d0de] p-7 flex flex-col gap-4"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-white">
                <i className={`${v.icon} text-[#C9A227] text-xl`}></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-[#222831] mb-2">{v.title}</h3>
                <p className="text-sm text-[#3A506B] leading-relaxed">{v.description}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
